import { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { fetchNodes } from '../api/fetchNodes';
import { fetchEdges } from '../api/fetchEdges';
import { extractGraphDataWithDepth } from './graph/extractGraphDataWithDepth';

export function useGraphData(depth, maxDepth = Infinity) {
  const { authFetch } = useContext(AuthContext);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [nodeDepthMap, setNodeDepthMap] = useState({});

  // Load Nodes
  const loadNodes = async (controller) => {
    try {
      const nodeJson = await fetchNodes(authFetch, depth, {
        signal: controller.signal,
      });

      const { nodes: extractedNodes, nodeDepthMap } = extractGraphDataWithDepth(
        nodeJson,
        maxDepth,
      );

      const nodesWithPosition = extractedNodes.map((n, i) => ({
        ...n,
        x_axis: n.x_axis ?? (i % 4) * 200 + 50,
        y_axis: n.y_axis ?? Math.floor(i / 4) * 150 + 50,
        rotation: n.rotation ?? Math.random() * 10 - 5,
      }));

      if (!controller.signal.aborted) {
        setNodes(nodesWithPosition);
        setNodeDepthMap(nodeDepthMap);
      }
    } catch (err) {
      if (err.name !== 'AbortError')
        console.error('Failed to load nodes:', err);
    }
  };

  // Load Edges
  const loadEdges = async (controller) => {
    try {
      const edgeJson = await fetchEdges(authFetch, {
        signal: controller.signal,
      });
      const adaptedEdges = edgeJson.map((e) => ({
        id: e.id,
        source: e.source_node,
        target: e.target_node,
        label: e.label,
      }));

      if (!controller.signal.aborted) {
        setEdges(adaptedEdges);
      }
    } catch (err) {
      if (err.name !== 'AbortError')
        console.error('Failed to load edges:', err);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadNodes(controller);
    loadEdges(controller);

    return () => controller.abort();
  }, [depth, maxDepth, authFetch]);

  return { nodes, setNodes, edges, setEdges, nodeDepthMap };
}
